import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Loader2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api-client';
import { Button } from '@ci-ville/ui';
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 8 * 1024 * 1024;
export function PhotoUploader({ maxFiles = 5, onChange }) {
    const [photos, setPhotos] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []); // eslint-disable-line react-hooks/exhaustive-deps
    const upload = useCallback(async (files) => {
        const valid = files.filter((f) => {
            if (!ACCEPTED.includes(f.type)) {
                toast.error(`${f.name} : format non accepté (JPEG, PNG ou WebP)`);
                return false;
            }
            if (f.size > MAX_SIZE) {
                toast.error(`${f.name} : trop volumineux (8 Mo maximum)`);
                return false;
            }
            return true;
        });
        const toUpload = valid.slice(0, maxFiles - photos.length);
        if (toUpload.length === 0)
            return;
        setUploading(true);
        const uploaded = [];
        for (const file of toUpload) {
            try {
                const { url, fields, key } = await api.uploads.presign(file.type);
                const form = new FormData();
                Object.entries(fields).forEach(([k, v]) => form.append(k, v));
                form.append('file', file);
                const res = await fetch(url, { method: 'POST', body: form });
                if (!res.ok)
                    throw new Error(`envoi refusé (${res.status})`);
                uploaded.push({ key, previewUrl: URL.createObjectURL(file), name: file.name });
            }
            catch (err) {
                toast.error(`${file.name} : ${err instanceof Error ? err.message : 'erreur d\'envoi'}`);
            }
        }
        setUploading(false);
        if (uploaded.length) {
            const next = [...photos, ...uploaded];
            setPhotos(next);
            onChange(next);
        }
    }, [maxFiles, photos, onChange]);
    const remove = (index) => {
        URL.revokeObjectURL(photos[index].previewUrl);
        const next = photos.filter((_, i) => i !== index);
        setPhotos(next);
        onChange(next);
    };
    return (_jsxs("div", { className: "space-y-2", children: [photos.length > 0 && (_jsx("div", { className: "grid grid-cols-4 gap-2 sm:grid-cols-5", children: photos.map((p, i) => (_jsxs("div", { className: "group relative aspect-square overflow-hidden rounded-md border border-border bg-muted", children: [_jsx("img", { src: p.previewUrl, alt: p.name, className: "h-full w-full object-cover" }), _jsx("button", { type: "button", onClick: () => remove(i), className: "absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100", title: "Retirer", children: _jsx(X, { className: "size-3" }) })] }, p.key))) })), photos.length < maxFiles && (_jsxs("div", { onDragOver: (e) => {
                    e.preventDefault();
                    setDragging(true);
                }, onDragLeave: () => setDragging(false), onDrop: (e) => {
                    e.preventDefault();
                    setDragging(false);
                    upload(Array.from(e.dataTransfer.files));
                }, className: `flex items-center gap-3 rounded-lg border-2 border-dashed p-3 transition-colors ${dragging ? 'border-brand bg-brand/5' : 'border-border bg-muted/20'}`, children: [uploading ? (_jsx(Loader2, { className: "size-5 animate-spin text-brand" })) : (_jsx("div", { className: "flex size-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand", children: _jsx(ImagePlus, { className: "size-4" }) })), _jsxs("div", { className: "min-w-0 flex-1 text-xs", children: [_jsx("p", { className: "font-medium", children: uploading ? 'Envoi en cours…' : 'Glissez des photos ici' }), _jsxs("p", { className: "text-muted-foreground", children: ["JPEG, PNG ou WebP \u00B7 8 Mo max \u00B7 ", maxFiles - photos.length, " restante(s)"] })] }), _jsxs(Button, { type: "button", size: "sm", variant: "outline", onClick: () => inputRef.current?.click(), disabled: uploading, className: "gap-1.5", children: [_jsx(Upload, { className: "size-3.5" }), "Parcourir"] }), _jsx("input", { ref: inputRef, type: "file", accept: ACCEPTED.join(','), multiple: true, hidden: true, onChange: (e) => {
                            if (e.target.files)
                                upload(Array.from(e.target.files));
                            e.target.value = '';
                        } })] }))] }));
}
//# sourceMappingURL=PhotoUploader.js.map